import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useAthlete } from '@/context/AthleteContext';
import { Calendar, CheckCircle, XCircle, Clock, Users, X, AlertCircle, User, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ParentSessions = () => {
  const { getAuthHeaders } = useAuth();
  const { athlete } = useAthlete();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmingSession, setConfirmingSession] = useState(null);
  const [showParticipants, setShowParticipants] = useState(null);
  const [participants, setParticipants] = useState(null);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [coaches, setCoaches] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [showCoachSelector, setShowCoachSelector] = useState(false);

  // Fetch available coaches for this athlete
  useEffect(() => {
    const fetchCoaches = async () => {
      if (!athlete) return;
      
      const availableCoaches = [];
      
      // Check for private coach
      if (athlete.coach_id) {
        availableCoaches.push({
          id: athlete.coach_id,
          type: 'private',
          name: athlete.coach_name || 'Private Coach',
          icon: User
        });
      }
      
      // Check for team coaches
      try {
        const teamsRes = await axios.get(`${API}/parent/athlete/${athlete.id}/teams`, { headers: getAuthHeaders() });
        if (teamsRes.data?.teams) {
          teamsRes.data.teams.forEach(team => {
            if (team.coach_id) {
              availableCoaches.push({
                id: team.coach_id,
                type: 'team',
                name: team.coach_name || `${team.team_name} Coach`,
                teamName: team.team_name,
                teamId: team.team_id,
                icon: Users
              });
            }
          });
        }
      } catch (err) {
        console.log('No team data');
      }
      
      setCoaches(availableCoaches);
      if (availableCoaches.length > 0 && !selectedCoach) {
        setSelectedCoach(availableCoaches[0]);
      }
    };
    
    fetchCoaches();
  }, [athlete]);

  const fetchData = async () => {
    if (!athlete || !selectedCoach) return;
    setLoading(true);
    try {
      let url = `${API}/athletes/${athlete.id}/sessions`;
      
      // If team coach, fetch team-specific sessions
      if (selectedCoach.type === 'team' && selectedCoach.teamId) {
        url = `${API}/parent/team/${selectedCoach.teamId}/athlete/${athlete.id}/sessions`;
      }
      
      const sessionsRes = await axios.get(url, { headers: getAuthHeaders() });
      setSessions(sessionsRes.data);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      setSessions([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [athlete, selectedCoach, getAuthHeaders]);

  const handleConfirmation = async (sessionId, action) => {
    setConfirmingSession(sessionId);
    try {
      await axios.post(`${API}/sessions/${sessionId}/confirm?action=${action}`, {}, { headers: getAuthHeaders() });
      toast.success(action === 'confirm' ? 'Participation confirmed!' : 'Session declined');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond');
    }
    setConfirmingSession(null);
  };

  const fetchParticipants = async (sessionId) => {
    setLoadingParticipants(true);
    try {
      const res = await axios.get(`${API}/sessions/${sessionId}/participants`, { headers: getAuthHeaders() });
      setParticipants(res.data);
      setShowParticipants(sessionId);
    } catch (error) {
      toast.error('Failed to load participants');
    }
    setLoadingParticipants(false);
  };

  const closeParticipantsModal = () => {
    setShowParticipants(null);
    setParticipants(null);
  };

  // Separate pending confirmation sessions from others
  const pendingSessions = sessions.filter(s => s.confirmation_status === 'pending' && s.status === 'scheduled');
  const otherSessions = sessions.filter(s => s.confirmation_status !== 'pending' || s.status !== 'scheduled');

  if (loading && sessions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parent-sessions">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl uppercase tracking-tight">Session History</h1>
          <p className="text-muted-foreground">{athlete?.name}'s training sessions</p>
        </div>
        
        {/* Coach Selector */}
        {coaches.length > 1 && (
          <div className="relative">
            <button
              onClick={() => setShowCoachSelector(!showCoachSelector)}
              className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors"
              data-testid="coach-selector"
            >
              {selectedCoach && (
                <>
                  <selectedCoach.icon size={18} className="text-primary" />
                  <span className="text-sm font-medium">{selectedCoach.name}</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-primary/20 text-primary">
                    {selectedCoach.type === 'private' ? 'Private' : 'Team'}
                  </span>
                </>
              )}
              <ChevronDown size={16} className={`transition-transform ${showCoachSelector ? 'rotate-180' : ''}`} />
            </button>
            
            {showCoachSelector && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-card border border-border rounded-lg shadow-lg z-50">
                <p className="px-3 py-2 text-xs text-muted-foreground border-b border-border">Select Coach View</p>
                {coaches.map((coach) => (
                  <button
                    key={`${coach.type}-${coach.id}`}
                    onClick={() => {
                      setSelectedCoach(coach);
                      setShowCoachSelector(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-3 hover:bg-muted/50 transition-colors ${
                      selectedCoach?.id === coach.id && selectedCoach?.type === coach.type ? 'bg-primary/10' : ''
                    }`}
                  >
                    <coach.icon size={18} className={selectedCoach?.id === coach.id ? 'text-primary' : 'text-muted-foreground'} />
                    <div className="text-left">
                      <p className="text-sm font-medium">{coach.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {coach.type === 'private' ? 'Private Coach' : coach.teamName}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pending Confirmations Section */}
      {pendingSessions.length > 0 && (
        <div className="space-y-4">
          <h2 className="font-heading text-xl text-yellow-400 flex items-center gap-2">
            <Clock size={20} />
            Awaiting Your Confirmation
          </h2>
          {pendingSessions.map((session) => (
            <div key={session.id} className="bg-yellow-500/10 border border-yellow-500/30 p-6 rounded-sm">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-3 h-3 rounded-full ${
                      session.session_type === 'clinic' ? 'bg-purple-400' : 'bg-blue-400'
                    }`}></div>
                    <h3 className="font-semibold capitalize flex items-center gap-2">
                      {session.session_type === 'group' || session.session_type === 'clinic' ? (
                        <Users size={16} className="text-purple-400" />
                      ) : null}
                      {session.session_type} Session
                    </h3>
                    {session.group_session_id && (
                      <button
                        onClick={() => fetchParticipants(session.id)}
                        className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded hover:bg-purple-500/30 transition-colors flex items-center gap-1"
                        title="View all participants"
                      >
                        <Users size={12} /> View Participants
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mb-1">
                    📅 {new Date(session.date).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      month: 'long', 
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                  {session.focus_points && (
                    <p className="text-sm"><span className="text-primary">Focus:</span> {session.focus_points}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleConfirmation(session.id, 'decline')}
                    disabled={confirmingSession === session.id}
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  >
                    <XCircle size={16} className="mr-1" />
                    Decline
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleConfirmation(session.id, 'confirm')}
                    disabled={confirmingSession === session.id}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <CheckCircle size={16} className="mr-1" />
                    {confirmingSession === session.id ? 'Processing...' : 'Confirm'}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* All Sessions */}
      {sessions.length === 0 ? (
        <div className="bg-card border border-border/40 p-12 rounded-sm text-center">
          <Calendar className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No sessions recorded yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendingSessions.length > 0 && otherSessions.length > 0 && (
            <h2 className="font-heading text-xl">Session History</h2>
          )}
          {otherSessions.map((session) => (
            <div key={session.id} className="bg-card border border-border/40 p-6 rounded-sm">
              <div className="flex items-center justify-between mb-2">
                <div className="flex flex-wrap items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${
                    session.status === 'cancelled' ? 'bg-red-400' :
                    session.status === 'completed' ? 'bg-green-400' :
                    session.session_type === 'private' ? 'bg-green-400' :
                    session.session_type === 'zoom' ? 'bg-blue-400' : 'bg-purple-400'
                  }`}></div>
                  <h3 className="font-semibold capitalize">{session.session_type} Session</h3>
                  {session.is_addon && (
                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">Add-on</span>
                  )}
                  {session.group_session_id && (
                    <button
                      onClick={() => fetchParticipants(session.id)}
                      className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded hover:bg-purple-500/30 transition-colors flex items-center gap-1"
                      title="View all participants"
                    >
                      <Users size={12} /> Participants
                    </button>
                  )}
                  {session.confirmation_status === 'confirmed' && (
                    <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded flex items-center gap-1">
                      <CheckCircle size={12} /> Confirmed
                    </span>
                  )}
                  {session.confirmation_status === 'declined' && (
                    <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">Declined</span>
                  )}
                  {session.status === 'cancelled' && (
                    <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">Cancelled</span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {new Date(session.date).toLocaleDateString('en-US', { 
                    weekday: 'short', 
                    month: 'short', 
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </p>
              </div>
              
              {session.focus_points && (
                <p className="text-sm mb-2">
                  <span className="text-primary">Focus:</span> {session.focus_points}
                </p>
              )}
              
              {session.notes && (
                <p className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-sm mt-2">
                  {session.notes}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {athlete && (
        <div className="bg-primary/10 border border-primary/30 p-4 rounded-sm text-center">
          <p className="text-lg font-semibold">{athlete.sessions_remaining} sessions remaining</p>
          <p className="text-sm text-muted-foreground">in your current plan</p>
        </div>
      )}

      {/* Participants Modal for Group Sessions */}
      {showParticipants && participants && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border p-6 rounded-sm w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl uppercase flex items-center gap-2">
                <Users size={20} className="text-purple-400" />
                Session Participants
              </h2>
              <button onClick={closeParticipantsModal} className="text-muted-foreground hover:text-foreground">
                <X size={20} />
              </button>
            </div>
            
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="bg-green-500/10 border border-green-500/30 p-3 rounded text-center">
                <p className="text-2xl font-bold text-green-400">{participants.confirmed || 0}</p>
                <p className="text-xs text-muted-foreground">Confirmed</p>
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/30 p-3 rounded text-center">
                <p className="text-2xl font-bold text-yellow-400">{participants.pending || 0}</p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 p-3 rounded text-center">
                <p className="text-2xl font-bold text-red-400">{participants.declined || 0}</p>
                <p className="text-xs text-muted-foreground">Declined</p>
              </div>
            </div>

            {/* Participants List */}
            <div className="space-y-2">
              {participants.participants?.map((p) => (
                <div 
                  key={p.session_id} 
                  className={`flex items-center justify-between p-3 rounded border ${
                    p.confirmation_status === 'confirmed' ? 'bg-green-500/5 border-green-500/30' :
                    p.confirmation_status === 'declined' ? 'bg-red-500/5 border-red-500/30' :
                    'bg-yellow-500/5 border-yellow-500/30'
                  } ${p.athlete_id === athlete?.id ? 'ring-2 ring-primary/50' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      p.confirmation_status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                      p.confirmation_status === 'declined' ? 'bg-red-500/20 text-red-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {p.athlete_name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        {p.athlete_name}
                        {p.athlete_id === athlete?.id && (
                          <span className="text-xs text-primary">(You)</span>
                        )}
                      </p>
                      {p.confirmed_at && (
                        <p className="text-xs text-muted-foreground">
                          Confirmed: {new Date(p.confirmed_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {p.confirmation_status === 'confirmed' && (
                      <span className="flex items-center gap-1 text-green-400 text-sm">
                        <CheckCircle size={16} /> Confirmed
                      </span>
                    )}
                    {p.confirmation_status === 'pending' && (
                      <span className="flex items-center gap-1 text-yellow-400 text-sm">
                        <Clock size={16} /> Pending
                      </span>
                    )}
                    {p.confirmation_status === 'declined' && (
                      <span className="flex items-center gap-1 text-red-400 text-sm">
                        <AlertCircle size={16} /> Declined
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <Button 
              onClick={closeParticipantsModal} 
              className="w-full mt-4 bg-primary text-black"
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentSessions;
